import { ApolloError, useQuery } from '@apollo/client'
import { useState } from 'react'
import { WaveKpiChangesQuery } from '../../../../data/gql-gen/questionnaire/graphql'
import { LoggerErrorType } from '../../../../data/logger'
import { useOptionalWaveId } from '../../../../hooks/useOptionalWaveId'
import { useHasEarlierWaves } from '../../../../modules/Questionnaire/Kpi/HasEarlierWavesContext'
import { captureApolloError } from '../../../../utils/HelperFunctions'
import { WAVE_KPI_CHANGES } from './WaveKpiChanges.questionnaire.gql'

export type KpiChanges = WaveKpiChangesQuery['kpiChangesV2']
export type KpiChangesStatus = 'none' | 'loading' | 'error' | 'changes'

const acknowledgementFingerprint = (kpiChanges: KpiChanges | undefined) => {
  const needsAcknowledgement =
    kpiChanges?.subjects.flatMap(({ trackingId, changes }) =>
      changes
        .filter((change) => change.needsAcknowledgement)
        .map((change) => ({ trackingId, ...change }))
    ) ?? []
  return needsAcknowledgement.length > 0
    ? JSON.stringify([kpiChanges?.previousWave?.waveId, needsAcknowledgement])
    : ''
}

export const useWaveKpiChanges = (projectId: string) => {
  const waveId = useOptionalWaveId()
  const hasEarlierWaves = useHasEarlierWaves()
  const skip = !waveId || !hasEarlierWaves
  const [acknowledgedFingerprint, setAcknowledgedFingerprint] = useState<
    string | undefined
  >()
  const [overriddenError, setOverriddenError] = useState<ApolloError>()
  const [retrying, setRetrying] = useState(false)
  const [rechecking, setRechecking] = useState(false)
  const [listUpdated, setListUpdated] = useState(false)

  const { data, loading, error, refetch } = useQuery(WAVE_KPI_CHANGES, {
    context: { clientName: 'questionnaire' },
    variables: { projectId, waveId: waveId ?? '' },
    fetchPolicy: 'no-cache',
    skip,
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloQuery, 'kpiChangesV2', error)
    }
  })
  const kpiChanges = data?.kpiChangesV2
  const fingerprint = acknowledgementFingerprint(kpiChanges)

  const getStatus = (): KpiChangesStatus => {
    if (skip) return 'none'
    if (retrying) return 'loading'
    if (error) return 'error'
    if (loading || !kpiChanges) return 'loading'
    if (!kpiChanges.previousWave || kpiChanges.subjects.length === 0)
      return 'none'
    return 'changes'
  }
  const status = getStatus()

  const requiresAcknowledgement =
    status === 'changes' && !!kpiChanges?.requiresAcknowledgement
  const acknowledged = acknowledgedFingerprint === fingerprint
  const launchWithoutChecking = !!error && overriddenError === error

  const getBlocksLaunch = () => {
    if (status === 'loading') return true
    if (status === 'error') return !launchWithoutChecking
    return requiresAcknowledgement && !acknowledged
  }

  const setAcknowledged = (checked: boolean) => {
    setAcknowledgedFingerprint(checked ? fingerprint : undefined)
    if (checked) setListUpdated(false)
  }

  const setLaunchWithoutChecking = (checked: boolean) =>
    setOverriddenError(checked ? error : undefined)

  const tryAgain = async () => {
    setRetrying(true)
    try {
      await refetch()
    } catch {
      // The query's own error state shows it.
    } finally {
      setRetrying(false)
    }
  }

  const recheck = async () => {
    if (skip || launchWithoutChecking) return true
    setRechecking(true)
    setListUpdated(false)
    try {
      const { data: latest } = await refetch()
      if (acknowledgementFingerprint(latest.kpiChangesV2) === fingerprint) {
        return true
      }
      setAcknowledgedFingerprint(undefined)
      setListUpdated(true)
      return false
    } catch {
      return false
    } finally {
      setRechecking(false)
    }
  }

  return {
    status,
    kpiChanges,
    error,
    requiresAcknowledgement,
    acknowledged,
    setAcknowledged,
    launchWithoutChecking,
    setLaunchWithoutChecking,
    listUpdated,
    tryAgain,
    recheck,
    rechecking,
    blocksLaunch: getBlocksLaunch()
  }
}

export type WaveKpiChangesState = ReturnType<typeof useWaveKpiChanges>
